import { z } from 'zod';
import { somarDinheiro, somarQuantidade } from './aritmetica.ts';
import {
  custoUnitarioSchema,
  dataDoFatoSchema,
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  paginacaoSchema,
  quantidadeSchema,
  respostaListaSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Transferência de estoque entre depósitos — na prática, entre obras.
 *
 * A `.spec/015`, decisão 8, deixou este documento explicitamente de fora com uma
 * frase que já descrevia o desenho: "é uma saída num local pelo custo médio dele
 * e uma entrada no outro por esse mesmo valor". A `.spec/016`, seção 5, repetiu a
 * regra e disse quando a tabela entraria: "se e quando a transferência entre
 * obras virar documento". É o que este arquivo faz.
 *
 * **Cada obra tem o depósito dela** (`depositos.obra_id`, `.spec/015`, decisão 7
 * revertida), então transferir material entre canteiros é transferir entre os
 * dois depósitos. O documento fala de depósito, e não de obra, porque é o
 * depósito que tem saldo — e porque o mesmo documento serve para mandar material
 * do centro de distribuição para o canteiro, que é a outra metade dos casos.
 *
 * **Mestre e itens num payload só**, como a solicitação de compra: o caminhão sai
 * do canteiro A com dez materiais, e dez documentos separados fariam dez vezes a
 * mesma digitação de origem, destino e data. Uma rota separada para os itens
 * deixaria existir transferência sem item nenhum entre duas chamadas HTTP — e
 * essa é a linha que não move saldo nenhum.
 *
 * **O efeito é imediato**, e não em duas etapas. Envio e recebimento separados
 * responderiam "o que está no caminhão", e o preço seria um depósito de trânsito
 * ou uma situação a conciliar. Foi avaliado e ficou de fora: o material entre
 * dois canteiros da mesma empresa chega no mesmo dia, e o estado intermediário
 * custaria uma tela de conferência que ninguém pediu. Se a viagem passar a durar,
 * é aqui que a situação entra.
 */

/* -------------------------------------------------------------------------- */
/* Códigos e rótulos                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Teto de itens por transferência.
 *
 * Não é julgamento sobre o negócio: existe para que um `curl` com dez mil linhas
 * não vire dez mil `INSERT` — e aqui vinte mil movimentações, porque cada item
 * gera duas. Uma transferência real de canteiro tem algumas dezenas de linhas, e
 * 200 dá folga de uma ordem de grandeza. É o mesmo teto da solicitação.
 */
export const MAXIMO_ITENS_TRANSFERENCIA = 200;

/* -------------------------------------------------------------------------- */
/* Itens                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Quantidade transferida: **sempre positiva**.
 *
 * O sentido é o par origem/destino, nunca o sinal do número. Aceitar negativo
 * criaria duas formas de dizer a mesma coisa — "mandar −10 de A para B" e
 * "mandar 10 de B para A" —, e a saída teria de decidir de qual depósito baixar.
 */
const quantidadeTransferidaSchema = quantidadeSchema.refine(
  (v) => Number(v) > 0,
  'A quantidade transferida deve ser maior que zero.',
);

/**
 * Item no payload de gravação.
 *
 * **Não tem valor**, e a coluna tem dois. `valorUnitario` é o custo médio vigente
 * no depósito de **origem** no instante da gravação, e `valorTotal` é o que sai
 * de lá — os dois calculados pelo servidor. Pedi-los ao usuário deixaria o
 * documento contradizer a baixa que o saldo sofreu, que é a mesma decisão do
 * lançamento de consumo.
 *
 * **Não tem depósito.** Origem e destino são do cabeçalho: um item que
 * escolhesse o próprio par transformaria o documento numa lista de
 * transferências independentes, e o romaneio do caminhão deixaria de descrever
 * uma viagem.
 *
 * **Não tem `id`.** Não há `PUT` — ver o comentário do documento —, então o
 * identificador do item nunca volta pelo payload.
 */
export const itemTransferenciaEstoqueEntradaSchema = z.object({
  /** Insumo transferido. Sempre material: serviço não movimenta estoque. */
  insumoId: uuidSchema,
  quantidade: quantidadeTransferidaSchema,
  observacao: z.string().trim().max(500, 'A observação tem no máximo 500 caracteres.').optional(),
});

export type ItemTransferenciaEstoqueEntrada = z.infer<typeof itemTransferenciaEstoqueEntradaSchema>;

/**
 * O mesmo insumo duas vezes é sempre digitação repetida.
 *
 * Origem e destino são do cabeçalho, então duas linhas do mesmo material movem o
 * mesmo par de saldos: a soma delas é o que o usuário queria numa só. Mantê-las
 * separadas faria a segunda linha ser baixada por um custo médio que a primeira
 * acabou de mudar — dois valores diferentes para o mesmo material no mesmo
 * documento, sem nada que explicasse a diferença.
 *
 * O `UNIQUE (tenant_id, transferencia_estoque_id, insumo_id)` recusa no banco;
 * aqui a recusa chega presa à linha certa, que é o que permite ao formulário
 * destacá-la.
 */
function validarItensDistintos(
  itens: ItemTransferenciaEstoqueEntrada[],
  ctx: z.RefinementCtx,
): void {
  const vistos = new Set<string>();

  itens.forEach((item, indice) => {
    if (!vistos.has(item.insumoId)) {
      vistos.add(item.insumoId);
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [indice, 'insumoId'],
      message: 'Este insumo já está na transferência. Some as quantidades numa linha só.',
    });
  });
}

const itensSchema = z
  .array(itemTransferenciaEstoqueEntradaSchema)
  .min(1, 'A transferência precisa de pelo menos um item.')
  .max(
    MAXIMO_ITENS_TRANSFERENCIA,
    `A transferência tem no máximo ${MAXIMO_ITENS_TRANSFERENCIA} itens.`,
  )
  .superRefine(validarItensDistintos);

/** Item como a API o devolve, com o insumo resolvido e os valores calculados. */
export const itemTransferenciaEstoqueSchema = z.object({
  id: uuidSchema,
  insumoId: uuidSchema,
  insumoNome: z.string().nullable(),
  unidadeSigla: z.string().nullable(),
  /** Strings decimais, nunca number: ponto flutuante perde casas em silêncio. */
  quantidade: quantidadeSchema,
  /**
   * Custo médio vigente na **origem** no instante da transferência, com 6 casas.
   *
   * Seis porque esta coluna **é** o custo médio, e ele tem seis — ver
   * `custoUnitarioSchema`. É o mesmo valor que entra no destino: transferir não
   * forma custo, só o muda de lugar.
   */
  valorUnitario: custoUnitarioSchema,
  /** O que saiu da origem e entrou no destino, a centavo. O total não muda. */
  valorTotal: dinheiroSchema,
  observacao: z.string().nullable(),
});

export type ItemTransferenciaEstoque = z.infer<typeof itemTransferenciaEstoqueSchema>;

/* -------------------------------------------------------------------------- */
/* Documento                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Payload de gravação.
 *
 * **Não tem `numero`**: quem o atribui é o contador da conta, dentro da
 * transação que grava o documento (`sequencias_documento`). Aceitá-lo do cliente
 * deixaria duas transferências nascerem com o mesmo número, e a segunda
 * descobriria isso no índice único, como erro de banco.
 *
 * A empresa é **uma só**, e vale para os dois depósitos: material que troca de
 * pessoa jurídica é fato fiscal e pede nota de transferência, não lançamento de
 * estoque. A checagem é do service — o banco não alcança a empresa de um depósito
 * a partir daqui.
 */
export const criarTransferenciaEstoqueSchema = z
  .object({
    /** Empresa do grupo — `pessoas` com `is_empresa`. É a dona dos dois depósitos. */
    empresaId: uuidSchema,
    /** Dia da transferência. Data de negócio, sem hora: o movimento é do dia 10. */
    dataLancamento: dataDoFatoSchema,
    /** De onde o material sai. É o custo médio deste depósito que vale. */
    depositoOrigemId: uuidSchema,
    /** Para onde o material vai. Na transferência entre obras, o canteiro destino. */
    depositoDestinoId: uuidSchema,
    itens: itensSchema,
    /**
     * Obrigatória — e, desde `.spec/015` decisão 52, a **única** observação
     * obrigatória do estoque.
     *
     * O lançamento manual e o consumo deixaram de exigi-la porque neles o tipo
     * de movimentação escolhido já diz o que aconteceu. Aqui não há esse campo:
     * o tipo da transferência é fixo (o par saída/entrada que o documento cria),
     * então nenhum outro campo explica **por que** o material trocou de galpão —
     * e transferência é justamente o movimento que alguém pergunta seis meses
     * depois por que aconteceu.
     */
    observacao: z
      .string()
      .trim()
      .min(3, 'Escreva o motivo da transferência.')
      .max(1000, 'A observação tem no máximo 1000 caracteres.'),
  })
  .superRefine((valor, ctx) => {
    /*
      Origem e destino diferentes.

      O `CHECK` do banco recusa a mesma linha, e a recusa daqui existe para chegar
      presa ao campo: sem ela o erro voltaria como violação de constraint, com
      texto de Postgres num formulário que tem os dois seletores lado a lado.

      No destino, e não na origem: quem monta o documento escolhe a origem
      primeiro, e é o segundo campo que está errado.
    */
    if (valor.depositoOrigemId === valor.depositoDestinoId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['depositoDestinoId'],
        message: 'O destino tem de ser diferente da origem.',
      });
    }
  });

export type CriarTransferenciaEstoque = z.infer<typeof criarTransferenciaEstoqueSchema>;

/**
 * Transferência como a API a devolve.
 *
 * Os nomes de empresa, depósitos e obras vêm **desnormalizados**: a listagem
 * mostra "Residencial Aurora → Jardim das Palmeiras" e não dois uuid, e resolver
 * isso no cliente significaria duas consultas por linha.
 */
export const transferenciaEstoqueSchema = z.object({
  id: uuidSchema,
  /**
   * O número sequencial do documento, **do servidor**.
   *
   * Existe porque a transferência viaja com o caminhão: o almoxarife do destino
   * diz "chegou a 412", e o `id` não serve para isso — uuidv7 tem 36 caracteres e
   * nenhuma ordem visível. Único na conta inteira, independentemente da empresa
   * do grupo, como na solicitação e no pedido.
   */
  numero: z.number().int(),
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  dataLancamento: dataSchema,
  depositoOrigemId: uuidSchema,
  depositoOrigemNome: z.string().nullable(),
  /**
   * A obra do depósito de origem, quando ele é de um canteiro.
   *
   * Nula no Estoque Geral e em todo depósito cadastrado à mão. Vem resolvida
   * porque é o que a tela mostra: "a transferência foi da Aurora para a
   * Palmeiras" se lê, e "do depósito 3 para o depósito 7" não.
   */
  obraOrigemNome: z.string().nullable(),
  depositoDestinoId: uuidSchema,
  depositoDestinoNome: z.string().nullable(),
  obraDestinoNome: z.string().nullable(),
  itens: z.array(itemTransferenciaEstoqueSchema),
  observacao: z.string(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type TransferenciaEstoque = z.infer<typeof transferenciaEstoqueSchema>;

export const listaTransferenciasEstoqueSchema = respostaListaSchema(transferenciaEstoqueSchema);
export type ListaTransferenciasEstoque = z.infer<typeof listaTransferenciasEstoqueSchema>;

/* -------------------------------------------------------------------------- */
/* Listagem                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Filtros da listagem.
 *
 * Sem `ativo`: documento de movimento não se inativa — ele existe ou não existe,
 * e enquanto existe mexeu no saldo dos dois depósitos.
 *
 * `depositoId` alcança as **duas pontas** de propósito. Quem abre a tela pergunta
 * "o que entrou e saiu deste canteiro", e dois filtros separados obrigariam a
 * fazer a mesma busca duas vezes para responder isso. Quem quiser um lado só usa
 * `depositoOrigemId` ou `depositoDestinoId`.
 */
export const listarTransferenciasEstoqueQuerySchema = paginacaoSchema.omit({ q: true }).extend({
  /**
   * O número do documento, exato.
   *
   * É a busca que substitui o `q`: quem procura uma transferência a procura pelo
   * número, que é o que está escrito no papel que veio com o caminhão. Exato
   * porque o número é curto — "43" como "contém" traria 43, 143 e 430, e a lista
   * deixaria de responder a pergunta.
   */
  numero: z.coerce.number().int().min(1).optional(),
  empresaId: uuidSchema.optional(),
  /** Qualquer uma das duas pontas. Ver o comentário do schema. */
  depositoId: uuidSchema.optional(),
  depositoOrigemId: uuidSchema.optional(),
  depositoDestinoId: uuidSchema.optional(),
  /**
   * O material transferido, por id.
   *
   * Alcança os **itens**, e não o cabeçalho: a transferência não tem insumo, cada
   * linha dela tem. É `EXISTS` no servidor, não junção — a junção traria o
   * documento repetido uma vez por item e a paginação passaria a contar itens.
   */
  insumoId: uuidSchema.optional(),
  /** Início do período, inclusivo. */
  de: dataSchema.optional(),
  /** Fim do período, inclusivo. */
  ate: dataSchema.optional(),
});

export type ListarTransferenciasEstoqueQuery = z.infer<
  typeof listarTransferenciasEstoqueQuerySchema
>;

export const COLUNAS_ORDENAVEIS_TRANSFERENCIA_ESTOQUE = [
  'numero',
  'dataLancamento',
  'createdAt',
] as const;

/* -------------------------------------------------------------------------- */
/* Regras compartilhadas entre a tela e a API                                 */
/* -------------------------------------------------------------------------- */

/**
 * Quantidade e valor somados do documento.
 *
 * Vive no contrato, e não na tela, porque as duas pontas mostram o mesmo número:
 * a listagem resume a transferência numa linha, e o formulário mostra o total
 * enquanto o usuário confere os itens. Duas implementações divergiriam num
 * arredondamento, e a divergência apareceria como total que muda ao salvar.
 *
 * A conta é sobre inteiros escalados (`somarDinheiro`, `somarQuantidade`), e não
 * sobre `Number`: somar quinze linhas de centavos em ponto flutuante erra na
 * segunda casa, e é dinheiro que sai de um depósito e entra no outro.
 *
 * **A quantidade só significa alguma coisa quando as unidades coincidem** — dez
 * sacos mais dois metros somam doze de nada. Ela vem assim mesmo porque a tela a
 * usa como contagem de volume do romaneio, ao lado do número de itens, e não como
 * grandeza física. O valor, esse é sempre em reais.
 */
export function totaisDaTransferencia(
  itens: readonly { quantidade: string; valorTotal: string }[],
): { quantidade: string; valorTotal: string } {
  return {
    quantidade: somarQuantidade(itens.map((item) => item.quantidade)),
    valorTotal: somarDinheiro(itens.map((item) => item.valorTotal)),
  };
}
